import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Select } from '@/components/ui';
import Badge from '@/components/ui/Badge';
import { useAuthContext } from '@/hooks/useAuthContext';
import { post } from '@/lib/api-client';
import type { WorkflowStatus } from '@/types';

interface WorkflowStatusEditorProps {
  holdRequestId: string;
  currentStatus?: WorkflowStatus;
}

const WORKFLOW_STATUS_OPTIONS: { value: WorkflowStatus; label: string }[] = [
  { value: 'new_submitted', label: 'New / Submitted' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'pending_response', label: 'Pending Response' },
  { value: 'completed', label: 'Completed' },
  { value: 'reopened', label: 'Reopened' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'exception', label: 'Exception' },
  { value: 'partial_response_received', label: 'Partial Response Received' },
];

const getStatusBadgeColor = (status: WorkflowStatus): string => {
  const colors: Record<WorkflowStatus, string> = {
    new_submitted: 'bg-slate-100 text-slate-800 border border-slate-300',
    in_progress: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
    pending_response: 'bg-orange-100 text-orange-800 border border-orange-300',
    completed: 'bg-emerald-100 text-emerald-800 border border-emerald-300',
    reopened: 'bg-violet-100 text-violet-800 border border-violet-300',
    cancelled: 'bg-neutral-200 text-neutral-600 border border-neutral-400',
    exception: 'bg-rose-100 text-rose-800 border border-rose-300',
    partial_response_received: 'bg-cyan-100 text-cyan-800 border border-cyan-300',
  };
  return colors[status] || 'bg-neutral-100 text-neutral-900';
};

export function WorkflowStatusEditor({
  holdRequestId,
  currentStatus,
}: WorkflowStatusEditorProps) {
  const { user } = useAuthContext();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<WorkflowStatus>(currentStatus || 'new_submitted');

  // Check if user can edit workflow status
  const canEdit = user?.role === 'charting_member' || user?.role === 'charting_manager';

  const updateStatusMutation = useMutation({
    mutationFn: async (newStatus: WorkflowStatus) => {
      const response = await post<{ success: boolean; message: string }>(
        '/charting/update-detailed-status',
        {
          holdRequestId,
          workflowStatus: newStatus,
        }
      );
      return response;
    },
    onSuccess: () => {
      // Invalidate queries to refetch data
      queryClient.invalidateQueries({ queryKey: ['holdRequest', holdRequestId] });
      queryClient.invalidateQueries({ queryKey: ['holdRequests'] });
      setIsEditing(false);
    },
    onError: (error) => {
      console.error('Failed to update workflow status:', error);
      alert('Failed to update workflow status. Please try again.');
      setSelectedStatus(currentStatus || 'new_submitted'); // Reset to current status
    },
  });

  const handleSave = () => {
    if (selectedStatus !== (currentStatus || 'new_submitted')) {
      updateStatusMutation.mutate(selectedStatus);
    } else {
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setSelectedStatus(currentStatus || 'new_submitted');
    setIsEditing(false);
  };

  const statusLabel = WORKFLOW_STATUS_OPTIONS.find(opt => opt.value === (currentStatus || 'new_submitted'))?.label || (currentStatus || 'new_submitted');

  if (!canEdit) {
    // Read-only view for users without edit permissions
    return (
      <div>
        <label className="text-xs font-semibold text-neutral-400 uppercase tracking-wide block mb-2">
          Workflow Status
        </label>
        <Badge className={getStatusBadgeColor(currentStatus || 'new_submitted')}>
          {statusLabel}
        </Badge>
      </div>
    );
  }

  if (!isEditing) {
    // Display mode with edit button
    return (
      <div>
        <label className="text-xs font-semibold text-neutral-400 uppercase tracking-wide block mb-2">
          Workflow Status
        </label>
        <div className="flex items-center gap-2">
          <Badge className={getStatusBadgeColor(currentStatus || 'new_submitted')}>
            {statusLabel}
          </Badge>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsEditing(true)}
            className="text-xs"
          >
            Change
          </Button>
        </div>
      </div>
    );
  }

  // Edit mode
  return (
    <div>
      <label className="text-xs font-semibold text-neutral-400 uppercase tracking-wide block mb-2">
        Workflow Status
      </label>
      <div className="space-y-2">
        <Select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value as WorkflowStatus)}
          options={WORKFLOW_STATUS_OPTIONS}
          className="w-full"
        />
        <div className="flex gap-2">
          <Button
            onClick={handleSave}
            disabled={updateStatusMutation.isPending}
            size="sm"
          >
            {updateStatusMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
          <Button
            variant="secondary"
            onClick={handleCancel}
            disabled={updateStatusMutation.isPending}
            size="sm"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
